import { type MutableRefObject, type RefObject, useEffect } from 'react';
import { Swiper as SwiperType } from 'swiper/types';

export interface UseKeepChevronsFocusableOptions {
  swiperRef: RefObject<SwiperType | null>;
  navigationEnabled: boolean;
  isSwiperReady: boolean;
  /** Set while Enter/Space on a chevron drives a slide so container snap can defer. */
  chevronKeyboardNavRef?: MutableRefObject<boolean>;
}

export const isChevronActivationKey = (event: KeyboardEvent): boolean =>
  event.key === 'Enter' || event.key === ' ' || event.code === 'Space';

export const applyChevronFocusOverrides = (el: HTMLButtonElement): void => {
  if (el.disabled) {
    el.disabled = false;
  }
  if (el.getAttribute('tabindex') === '-1') {
    el.setAttribute('tabindex', '0');
  }
  if (el.getAttribute('aria-disabled') === 'true') {
    el.setAttribute('aria-disabled', 'false');
  }
};

const CHEVRON_ATTRS = ['disabled', 'tabindex', 'aria-disabled'] as const;

const isChevron = (el: unknown, chevrons: HTMLButtonElement[]): el is HTMLButtonElement =>
  el instanceof HTMLButtonElement && chevrons.includes(el);

const isChevronFocused = (chevrons: HTMLButtonElement[]): HTMLButtonElement | undefined =>
  chevrons.find(el => document.activeElement === el);

const applyWhileFocused = (el: HTMLButtonElement): boolean => {
  const beforeDisabled = el.disabled;
  const beforeTabIndex = el.getAttribute('tabindex');
  const beforeAriaDisabled = el.getAttribute('aria-disabled');
  applyChevronFocusOverrides(el);
  return (
    beforeDisabled !== el.disabled ||
    beforeTabIndex !== el.getAttribute('tabindex') ||
    beforeAriaDisabled !== el.getAttribute('aria-disabled')
  );
};

const isFocusMovingToChevron = (
  relatedTarget: EventTarget | null,
  chevrons: HTMLButtonElement[],
): boolean => isChevron(relatedTarget, chevrons);

/**
 * Swiper marks navigation buttons `disabled` at `isBeginning` / `isEnd`. A focused
 * chevron that becomes disabled loses focus to `<body>`, which fires blur snap-back
 * (`slideTo(0, 0)`) and cancels an in-flight slide animation — most visible on the
 * last keyboard (Space) back press. Only override boundary attrs while the chevron
 * holds focus; re-sync from Swiper once focus moves away.
 */
export function useKeepChevronsFocusable({
  swiperRef,
  navigationEnabled,
  isSwiperReady,
  chevronKeyboardNavRef,
}: UseKeepChevronsFocusableOptions): void {
  useEffect(() => {
    if (!navigationEnabled || !isSwiperReady) return;
    const nav = swiperRef.current?.navigation;
    const prevEl = nav?.prevEl as HTMLButtonElement | null | undefined;
    const nextEl = nav?.nextEl as HTMLButtonElement | null | undefined;
    const chevrons = [prevEl, nextEl].filter(
      (el): el is HTMLButtonElement => el instanceof HTMLButtonElement,
    );
    if (chevrons.length === 0) return;

    const swiper = swiperRef.current;
    const containerCandidate = chevrons[0]?.closest('.vds-carousel__container');
    const container = containerCandidate instanceof HTMLElement ? containerCandidate : null;

    let needsRestore = false;
    let chevronNavInFlight = false;
    let syncRafId: number | null = null;

    const restore = (): void => {
      swiperRef.current?.navigation?.update?.();
    };

    const sync = (): void => {
      const focused = isChevronFocused(chevrons);
      if (focused) {
        if (applyWhileFocused(focused)) {
          needsRestore = true;
        }
        return;
      }
      if (chevronNavInFlight || chevronKeyboardNavRef?.current) return;
      if (needsRestore) {
        restore();
        needsRestore = false;
      }
    };

    const onTransitionStart = (): void => {
      if (isChevronFocused(chevrons) || chevronKeyboardNavRef?.current) {
        chevronNavInFlight = true;
      }
    };

    const onTransitionEnd = (): void => {
      if (!chevronNavInFlight) return;
      chevronNavInFlight = false;
      sync();
    };

    const scheduleSyncAfterFocusChange = (): void => {
      if (syncRafId !== null) {
        cancelAnimationFrame(syncRafId);
      }
      syncRafId = requestAnimationFrame(() => {
        syncRafId = null;
        sync();
      });
    };

    const onChevronFocusOut = (event: Event): void => {
      if (!(event instanceof FocusEvent)) return;
      if (!isChevron(event.target, chevrons)) return;
      if (isFocusMovingToChevron(event.relatedTarget, chevrons)) {
        if (needsRestore) {
          restore();
          needsRestore = false;
        }
        return;
      }
      scheduleSyncAfterFocusChange();
    };

    sync();

    const observers = chevrons.map(el => {
      const observer = new MutationObserver(sync);
      observer.observe(el, {
        attributes: true,
        attributeFilter: [...CHEVRON_ATTRS],
      });
      return observer;
    });

    for (const el of chevrons) {
      el.addEventListener('focusin', sync);
    }
    container?.addEventListener('focusout', onChevronFocusOut, true);
    swiper?.on('slideChangeTransitionStart', onTransitionStart);
    swiper?.on('slideChangeTransitionEnd', onTransitionEnd);

    return () => {
      if (syncRafId !== null) {
        cancelAnimationFrame(syncRafId);
      }
      swiper?.off('slideChangeTransitionStart', onTransitionStart);
      swiper?.off('slideChangeTransitionEnd', onTransitionEnd);
      for (const observer of observers) {
        observer.disconnect();
      }
      for (const el of chevrons) {
        el.removeEventListener('focusin', sync);
      }
      container?.removeEventListener('focusout', onChevronFocusOut, true);
    };
  }, [navigationEnabled, isSwiperReady, swiperRef, chevronKeyboardNavRef]);
}
